import range from "lodash/range";
import { jsx } from "panel";
import { SvgIconEl } from "common/components/svg-icon";
import { TooltipEl } from "common/components/tooltip";
import { Placement } from "common/components/tooltip/types";
import { PanelEl } from "common/components/panel";
import { Direction } from "common/components/panel/types";
import { handleKeyPress } from "common/util/focusable";
import { SidenavItemEl } from "./sidenav-item";
import { CtaIconTheme } from "./types";
import { ScrollPosition, SIDENAV_DEFAULT_WIDTH, SIDENAV_MAX_WIDTH, SIDENAV_MIN_WIDTH } from "./constants";
function Loader() {
  return /* @__PURE__ */ jsx("div", {
    sel: ".loader"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".mp-skeleton-header"
  }), range(9).map((_) => /* @__PURE__ */ jsx("div", {
    sel: ".mp-skeleton-large"
  })));
}
function SidenavSectionItemsWrapper({ isExpanded }, sectionItemsChildren) {
  if (isExpanded) {
    return /* @__PURE__ */ jsx("div", {
      sel: ".section-items-list"
    }, sectionItemsChildren);
  }
  return null;
}
function SidenavCtaEl({
  cta,
  isExpanded,
  isSelected,
  onClick
}) {
  const iconRenderer = () => {
    var _a;
    return /* @__PURE__ */ jsx("div", {
      sel: ".icon-wrapper",
      class: { [`${(_a = cta.iconTheme) != null ? _a : CtaIconTheme.Light}-theme`]: true, "is-selected": isSelected }
    }, /* @__PURE__ */ jsx(SvgIconEl, {
      attrs: { icon: cta.icon }
    }));
  };
  if (isExpanded) {
    return /* @__PURE__ */ jsx(SidenavItemEl, {
      sel: ".cta-item",
      props: { iconRenderer, item: cta },
      attrs: { "is-selected": isSelected },
      on: { itemClick: (ev) => onClick(ev.detail.value) }
    });
  } else {
    return /* @__PURE__ */ jsx("a", {
      sel: ".cta-item.collapsed",
      on: { click: () => onClick(cta.value), keypress: handleKeyPress },
      attrs: { value: cta.value, tabindex: 0, ...cta.href ? { href: cta.href } : {} }
    }, iconRenderer(), /* @__PURE__ */ jsx(TooltipEl, {
      attrs: { placement: Placement.Right }
    }, cta.label));
  }
}
function SidenavSection({
  section,
  isExpanded,
  sectionDragDisabled,
  onToggleSection
}, sectionItemsChildren) {
  const isSectionCollapsible = section.label && section.isCollapsible;
  return /* @__PURE__ */ jsx("div", {
    sel: ".section",
    class: { "active-dragging-disabled": sectionDragDisabled }
  }, isSectionCollapsible && /* @__PURE__ */ jsx("div", {
    sel: ".section-header",
    attrs: { tabindex: 0 },
    on: { click: () => onToggleSection(section.label), keypress: handleKeyPress }
  }, /* @__PURE__ */ jsx(SvgIconEl, {
    sel: ".caret",
    attrs: {
      icon: isExpanded ? `caret-down` : `caret-right`
    }
  }), /* @__PURE__ */ jsx("div", {
    sel: ".section-header-label"
  }, section.label)), /* @__PURE__ */ jsx(SidenavSectionItemsWrapper, {
    isExpanded: isSectionCollapsible ? isExpanded : true
  }, sectionItemsChildren));
}
export function template() {
  var _a;
  const allSelectedIndices = this.helpers.getAllSelectedIndices();
  const sections = this.state.sections;
  const sidenavContent = this.attr(`loading`) ? /* @__PURE__ */ jsx(Loader, null) : sections.map((section, sectionIdx) => {
    const sectionDragDisabled = Number.isInteger(this.state.activeDragSectionIdx) && this.state.activeDragSectionIdx !== sectionIdx;
    const isExpanded = !this.state.collapsedSectionSet.has(section.label);
    const { itemIndex: selectedSidenavIndex } = allSelectedIndices.find((selectedIndices) => selectedIndices.sectionIndex === sectionIdx) || {};
    const selectedItemInSection = selectedSidenavIndex > -1 ? sections[sectionIdx].items[selectedSidenavIndex] : null;
    if (section.items.length === 0) {
      return null;
    }
    return /* @__PURE__ */ jsx(SidenavSection, {
      section,
      isExpanded,
      sectionDragDisabled,
      onToggleSection: this.helpers.toggleSection
    }, isExpanded && section.items.map((item, idx) => {
      var _a2;
      return /* @__PURE__ */ jsx("div", {
        sel: ".sidenav-item",
        attrs: { value: item.value },
        hook: {
          insert: this.helpers.handleSidenavItemInserted
        }
      }, /* @__PURE__ */ jsx(SidenavItemEl, {
        attrs: {
          "is-selected": selectedItemInSection && item.value === selectedItemInSection.value,
          "is-new": item.isNew && !((_a2 = this.state.hoveredItemValues) == null ? void 0 : _a2.has(item.value)),
          "is-draggable": section.dragDropEnabled,
          "is-disabled": sectionDragDisabled,
          "can-drop-item": Number.isInteger(this.state.activeDragSectionIdx)
        },
        on: {
          itemDrop: this.helpers.onItemDrop,
          itemDragStart: this.helpers.onItemDragStart,
          itemDragEnd: this.helpers.onItemDragEnd,
          itemClick: this.helpers.onSelectItem,
          overflowMenuChange: this.helpers.onItemOverflowMenuChange,
          overflowMenuSelect: this.helpers.onItemOverflowMenuSelect,
          mouseenter: () => this.helpers.onItemMouseEntered(item)
        },
        props: { confirmNavigateFunc: this.confirmNavigateFunc, item: { ...item, itemIdx: idx, sectionIdx } }
      }));
    }));
  });
  const header = this.attr(`header`);
  const footer = this.attr(`footer`);
  return /* @__PURE__ */ jsx(PanelEl, {
    sel: ".sidebar",
    attrs: {
      "storage-key": this.attr(`storage-key`),
      "min-adjacent-size": this.attr(`min-adjacent-width`),
      "max-size": SIDENAV_MAX_WIDTH,
      "min-size": SIDENAV_MIN_WIDTH,
      "panel-name": `Side Nav`,
      "resize-direction": Direction.Right,
      "default-ratio": 0.15,
      "default-size": SIDENAV_DEFAULT_WIDTH,
      "fixed-size": this.attr(`fixed-size`),
      "show-collapse-button": !this.attr(`fixed-size`),
      ...(footer == null ? void 0 : footer.actions) ? { "extra-actions": footer == null ? void 0 : footer.actions } : {},
      "style-override": `.wrapper .content-footer { border-color: var(--gray2); }`
    },
    on: {
      panelResize: (ev) => {
        const width = ev.detail.size;
        const isCollapsed = width < SIDENAV_MIN_WIDTH;
        this.update({ isCollapsed });
        this.dispatchEvent(new CustomEvent(`sidenavResize`, { detail: { width } }));
      },
      actionclick: (ev) => this.helpers.onFooterActionClick(ev.detail.value)
    },
    props: { containerEl: this.parentElement }
  }, /* @__PURE__ */ jsx("div", {
    sel: ".wrapper",
    class: {
      collapsed: this.state.isCollapsed
    },
    attrs: { elref: `wrapper` }
  }, header ? /* @__PURE__ */ jsx("div", {
    sel: ".header",
    attrs: { elref: `sidenavHeader` },
    class: {
      "is-expanded-style": this.helpers.isExpandedStyle(),
      "bottom-border": ![ScrollPosition.Top, ScrollPosition.None].includes(this.state.scrollPosition) && this.helpers.isExpandedStyle(),
      "slot-header": `slot` in header,
      "actions-container": `actions` in header
    }
  }, !this.state.isCollapsed ? /* @__PURE__ */ jsx("div", {
    sel: ".title"
  }, header.title) : null, `slot` in header ? /* @__PURE__ */ jsx("slot", {
    attrs: {
      name: this.state.isCollapsed && header.collapsedSlot ? header.collapsedSlot : header.slot
    }
  }) : (_a = header.actions) == null ? void 0 : _a.map((cta) => /* @__PURE__ */ jsx(SidenavCtaEl, {
    cta,
    isExpanded: this.helpers.isExpandedStyle(),
    isSelected: this.attr(`selected`) === cta.value,
    onClick: this.helpers.onHeaderActionClick
  }))) : null, !this.state.isCollapsed ? /* @__PURE__ */ jsx("div", {
    sel: ".sections-container",
    hook: { insert: this.helpers.onSectionsInserted },
    attrs: { elref: `sectionsContainer` }
  }, /* @__PURE__ */ jsx("div", {
    sel: ".sections"
  }, sidenavContent)) : null));
}
